/**
 * useAuth Hook
 * Comprehensive React hook for authentication state and operations
 */

import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { 
  selectAuth,
  selectIsAuthenticated,
  selectIsLoading,
  selectUser,
  selectTenants,
  selectCurrentTenant,
  selectTenantPermissions,
  selectAuthError,
  selectTokenStatus,
  selectUserDisplayName,
  selectHasMultipleTenants,
  selectCanRetryLastOperation,
  selectAuthenticationReady
} from '../store/slices/authSlice';
import store from '../store';
import { authManager } from '../services/auth/authManager';
import { 
  LoginCredentials, 
  AuthResult, 
  User, 
  Permission, 
  UserPermission,
  UserPreferences, 
  Tenant, 
  AuthError,
  TokenStatus
} from '../types/auth';
import { StoredAccount } from '../features/auth/utils/accountStorage';
import { logger } from '../utils/logger';

export interface UseAuthReturn {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isReady: boolean;
  user: User | null;
  error: AuthError | null;
  tokenStatus: TokenStatus;
  
  // Tenant management
  tenants: Tenant[];
  currentTenant: Tenant | null;
  permissions: UserPermission[];
  hasMultipleTenants: boolean;
  
  // Multi-account management
  availableAccounts: StoredAccount[];
  currentAccountId: string | null;
  accountsLastUpdated: number;
  
  // Authentication operations
  login: (credentials: LoginCredentials) => Promise<AuthResult>;
  loginWithGoogle: (token: string) => Promise<AuthResult>;
  logout: () => Promise<AuthResult>;
  
  // Multi-account operations
  switchAccount: (accountId: string) => Promise<boolean>;
  addAccount: (credentials: LoginCredentials, options?: { rememberMe?: boolean; persistenceDays?: number }) => Promise<AuthResult>;
  removeAccount: (accountId: string) => Promise<boolean>;
  refreshAccounts: () => Promise<void>;
  
  // Session management
  initializeSession: () => Promise<boolean>;
  refreshTokens: () => Promise<boolean>;
  
  // Tenant operations
  switchTenant: (tenantId: string) => Promise<boolean>;
  validateTenantAccess: (tenantId: string) => Promise<boolean>;
  
  // Preferences management
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<boolean>;
  
  // Error handling
  clearError: () => void;
  canRetry: boolean;
  retryLastOperation: () => Promise<boolean>;
  
  // Utility functions
  hasPermission: (resource: string, action: string) => boolean;
  hasResourceAccess: (resource: string) => boolean;
  hasRole: (roleCode: string) => boolean;
  hasAdminRole: () => boolean;
  getUserDisplayName: () => string | null;
}

/**
 * Main authentication hook
 */
export function useAuth(): UseAuthReturn {
  const authState = useSelector(selectAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tenants = useSelector(selectTenants);
  const currentTenant = useSelector(selectCurrentTenant);
  const permissions = useSelector(selectTenantPermissions);
  const error = useSelector(selectAuthError);
  const tokenStatus = useSelector(selectTokenStatus);
  const userDisplayName = useSelector(selectUserDisplayName);
  const hasMultipleTenants = useSelector(selectHasMultipleTenants);
  const canRetry = useSelector(selectCanRetryLastOperation);
  const isReady = useSelector(selectAuthenticationReady);

  // Authentication operations
  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthResult> => {
    try {
      const result = await authManager.login(credentials);
      return result;
    } catch (error) {
      logger.error('Login failed via hook', error);
      return { success: false, error: 'An unexpected error occurred during login' };
    }
  }, []);

  const loginWithGoogle = useCallback(async (token: string): Promise<AuthResult> => {
    try {
      const result = await authManager.loginWithGoogle(token);
      return result;
    } catch (error) {
      logger.error('Google login failed via hook', error);
      return { success: false, error: 'An unexpected error occurred during Google login' };
    }
  }, []);

  const logout = useCallback(async (): Promise<AuthResult> => {
    try {
      await authManager.logout();
      return { success: true };
    } catch (error) {
      logger.error('Logout failed via hook', error);
      return { success: false, error: 'An unexpected error occurred during logout' };
    }
  }, []);

  // Session management
  const initializeSession = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authManager.initializeSession();
      return result.success;
    } catch (error) {
      logger.error('Session initialization failed via hook', error);
      return false;
    }
  }, []);

  const refreshTokens = useCallback(async (): Promise<boolean> => {
    // Token refresh is handled internally by the auth manager during login/initialization
    // For now, just return true as a placeholder
    return true;
  }, []);

  // Tenant operations
  const switchTenant = useCallback(async (tenantId: string): Promise<boolean> => {
    try {
      const result = await authManager.switchTenant(tenantId);
      return result.success;
    } catch (error) {
      logger.error('Tenant switch failed via hook', error);
      return false;
    }
  }, []);

  const validateTenantAccess = useCallback(async (tenantId: string): Promise<boolean> => {
    try {
      return await authManager.validateTenantAccess(tenantId);
    } catch (error) {
      logger.error('Tenant access validation failed via hook', error);
      return false;
    }
  }, []);

  // Preferences management
  const updatePreferences = useCallback(async (_newPreferences: Partial<UserPreferences>): Promise<boolean> => {
    try {
      // TODO: Implement preferences update using preferences slice
      logger.warn('updatePreferences not yet implemented with new preferences slice');
      return false;
    } catch (error) {
      logger.error('Preferences update failed via hook', error);
      return false;
    }
  }, []);

  // Error handling
  const clearError = useCallback(() => {
    authManager.clearLastError();
  }, []);

  const retryLastOperation = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authManager.retryLastOperation();
      return result.success;
    } catch (error) {
      logger.error('Retry operation failed via hook', error);
      return false;
    }
  }, []);

  // Utility functions
  const hasPermission = useCallback((resource: string, action: string): boolean => {
    // Handle structured permissions
    if (permissions.length > 0 && typeof permissions[0] === 'object' && 'resource' in permissions[0]) {
      return permissions.some(permission => {
        const structuredPermission = permission as Permission;
        return structuredPermission.resource === resource && structuredPermission.actions.includes(action);
      });
    }
    
    // Handle string permissions
    const permissionString = `${resource}.${action}`;
    return (permissions as string[]).includes(permissionString);
  }, [permissions]);

  const hasResourceAccess = useCallback((resource: string): boolean => {
    // Handle structured permissions
    if (permissions.length > 0 && typeof permissions[0] === 'object' && 'resource' in permissions[0]) {
      return permissions.some(permission => {
        const structuredPermission = permission as Permission;
        return structuredPermission.resource === resource;
      });
    }
    
    // Handle string permissions - check if any permission starts with the resource
    const stringPermissions = permissions as string[];
    return stringPermissions.some(permission => permission.startsWith(`${resource}.`));
  }, [permissions]);

  const hasRole = useCallback((roleCode: string): boolean => {
    if (!user?.roles) return false;
    
    return user.roles.some(role => {
      if (typeof role === 'string') {
        return role === roleCode || role.includes(roleCode);
      } else {
        return role.roleCode === roleCode || role.roleCode?.includes(roleCode);
      }
    });
  }, [user?.roles]);

  const hasAdminRole = useCallback((): boolean => {
    if (!user?.roles) return false;
    
    return user.roles.some(role => {
      if (typeof role === 'string') {
        return role.includes('admin') || role.includes('sys-admin');
      } else {
        return role.roleCode?.includes('admin') || role.roleName?.toLowerCase().includes('admin');
      }
    });
  }, [user?.roles]);

  const getUserDisplayName = useCallback((): string | null => {
    return userDisplayName;
  }, [userDisplayName]);

  // Multi-account state - use existing Redux store
  const [availableAccounts, setAvailableAccounts] = useState<StoredAccount[]>([]);
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null);
  const [accountsLastUpdated, setAccountsLastUpdated] = useState<number>(0);

  // Load available accounts and sync with Redux state
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const accounts = await authManager.getAvailableAccounts();
        setAvailableAccounts(accounts);
        
        const activeId = authManager.getCurrentAccountId();
        setCurrentAccountId(activeId);
        setAccountsLastUpdated(Date.now());
      } catch (error) {
        logger.error('Failed to load available accounts', error);
      }
    };

    loadAccounts();
  }, [isAuthenticated]);

  // Multi-account operations - SEQUENTIAL EXECUTION
  const switchAccount = useCallback(async (accountId: string): Promise<boolean> => {
    try {
      logger.auth('useAuth: Starting account switch', { accountId });
      
      // SEQUENTIAL: Execute account switch in authManager
      // This will call accountSwitchSuccess in Redux which updates all state atomically
      const result = await authManager.switchAccount(accountId);
      
      if (result.success) {
        logger.auth('useAuth: Account switch completed successfully', { accountId });
        
        // SEQUENTIAL: Update local state after Redux state is updated
        setCurrentAccountId(accountId);
        
        // SEQUENTIAL: Refresh accounts list
        const accounts = await authManager.getAvailableAccounts();
        setAvailableAccounts(accounts);
        setAccountsLastUpdated(Date.now());
      } else {
        logger.error('useAuth: Account switch failed', { accountId });
      }
      
      return result.success;
    } catch (error) {
      logger.error('Switch account failed via hook', error);
      return false;
    }
  }, []);

  const addAccount = useCallback(async (credentials: LoginCredentials, options?: { rememberMe?: boolean; persistenceDays?: number }): Promise<AuthResult> => {
    try {
      const result = await authManager.addAccount(credentials, options);
      if (result.success) {
        // Refresh accounts list
        const accounts = await authManager.getAvailableAccounts();
        setAvailableAccounts(accounts);
        setAccountsLastUpdated(Date.now());
      }
      return result;
    } catch (error) {
      logger.error('Add account failed via hook', error);
      return { success: false, error: 'An unexpected error occurred while adding account' };
    }
  }, []);

  const removeAccount = useCallback(async (accountId: string): Promise<boolean> => {
    try {
      const result = await authManager.removeAccount(accountId);
      if (result.success) {
        // Force immediate refresh of accounts list after removal
        logger.auth('useAuth: Account removed, forcing immediate refresh');
        
        const accounts = await authManager.getAvailableAccounts();
        setAvailableAccounts(accounts);
        
        // Update current account ID if it changed
        const activeId = authManager.getCurrentAccountId();
        setCurrentAccountId(activeId);
        setAccountsLastUpdated(Date.now());
        
        logger.auth('useAuth: Accounts refreshed immediately after removal', { 
          accountCount: accounts.length,
          activeId 
        });
      }
      return result.success;
    } catch (error) {
      logger.error('Remove account failed via hook', error);
      return false;
    }
  }, []);

  const refreshAccounts = useCallback(async (): Promise<void> => {
    try {
      logger.auth('useAuth: Refreshing accounts');
      console.log('useAuth: Starting accounts refresh');
      
      const accounts = await authManager.getAvailableAccounts();
      setAvailableAccounts(accounts);
      
      const activeId = authManager.getCurrentAccountId();
      setCurrentAccountId(activeId);
      setAccountsLastUpdated(Date.now());
      
      logger.auth('useAuth: Accounts refreshed successfully', { 
        accountCount: accounts.length,
        activeId 
      });
      
      console.log('useAuth: Accounts refreshed successfully', { 
        accountCount: accounts.length,
        activeId,
        timestamp: Date.now()
      });
    } catch (error) {
      logger.error('Refresh accounts failed via hook', error);
      console.error('useAuth: Refresh accounts failed', error);
    }
  }, []);

  // Listen for Redux state changes that indicate account operations
  const lastOperation = useSelector((state: any) => state.auth.lastOperation);
  
  useEffect(() => {
    // Only refresh accounts when specific operations complete, not on every auth state change
    if (lastOperation === 'account_switch_completed' || 
        lastOperation === 'account_added' || 
        lastOperation === 'account_removed') {
      refreshAccounts();
    }
  }, [lastOperation, refreshAccounts]); // Removed isAuthenticated to prevent excessive refreshing

  // Also listen for account removal events specifically
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      const currentLastOperation = state.auth.lastOperation;
      
      // Check if an account was just removed by looking for the accountRemoved action
      // This is a workaround since accountRemoved doesn't set lastOperation
      if (currentLastOperation === 'account_removed' || 
          (currentLastOperation && typeof currentLastOperation === 'string' && currentLastOperation.includes('account_removed'))) {
        logger.auth('useAuth: Detected account removal, refreshing accounts');
        refreshAccounts();
      }
    });

    return unsubscribe;
  }, [refreshAccounts]);

  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    isInitialized: authState.isInitialized,
    isReady,
    user,
    error,
    tokenStatus,
    
    // Tenant management
    tenants,
    currentTenant,
    permissions,
    hasMultipleTenants,
    
    // Multi-account management
    availableAccounts,
    currentAccountId,
    accountsLastUpdated,
    
    // Authentication operations
    login,
    loginWithGoogle,
    logout,
    
    // Multi-account operations
    switchAccount,
    addAccount,
    removeAccount,
    refreshAccounts,
    
    // Session management
    initializeSession,
    refreshTokens,
    
    // Tenant operations
    switchTenant,
    validateTenantAccess,
    
    // Preferences management
    updatePreferences,
    
    // Error handling
    clearError,
    canRetry,
    retryLastOperation,
    
    // Utility functions
    hasPermission,
    hasResourceAccess,
    hasRole,
    hasAdminRole,
    getUserDisplayName
  };
}

/**
 * Hook for authentication initialization
 * Re-exported from features/auth/hooks for convenience
 * DO NOT duplicate this logic here - it causes race conditions
 */
export { useAuthInitialization } from '../features/auth/hooks/useAuthInitialization';

/**
 * Hook for authentication loading states
 */
export function useAuthLoading() {
  const { isLoading } = useAuth();

  return {
    isLoading,
    isLoginLoading: isLoading,
    isLogoutLoading: isLoading,
    isInitializeLoading: isLoading,
    isTenantSwitchLoading: isLoading
  };
}

/**
 * Hook for authentication errors
 */
export function useAuthError() {
  const { error, clearError, canRetry, retryLastOperation } = useAuth();

  const handleRetry = useCallback(async () => {
    if (canRetry) {
      return await retryLastOperation();
    }
    return false;
  }, [canRetry, retryLastOperation]);

  return {
    error,
    hasError: error !== null,
    clearError,
    canRetry,
    retry: handleRetry,
    errorMessage: error?.userMessage || null,
    errorCode: error?.code || null,
    isRecoverable: error?.recoverable || false
  };
}

/**
 * Hook for protected routes
 */
export function useAuthGuard(requiredPermissions?: { resource: string; action: string }[]) {
  const { isAuthenticated, isReady, hasPermission, user } = useAuth();
  
  const hasRequiredPermissions = useCallback(() => {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }
    
    return requiredPermissions.every(({ resource, action }) => 
      hasPermission(resource, action)
    );
  }, [requiredPermissions, hasPermission]);

  const canAccess = isAuthenticated && isReady && hasRequiredPermissions();

  return {
    isAuthenticated,
    isReady,
    canAccess,
    user,
    hasRequiredPermissions: hasRequiredPermissions(),
    isLoading: !isReady
  };
}